<template>
  <div>
    <valkyrie-list
      item-key="id"
      sort-by="property_name"
      sortable
      :list-data="listData"
      :pagination="propertyZonesListPagination"
      :pagination-text="paginationText"
      :list-name="'Zones'"
      :headers.sync="headers"
      :is-loading="loading"
      @update-sort="handleUpdateSort"
      @update-pagination="handleUpdatePagination"
    >
      <template v-slot:list-header>
        <default-list-header
          :searchable="false"
          :filterable="filterable"
          :pagination-text="paginationText"
          :exportable="false"
          @clear-filters="handleClearFilters"
        >
          <template #city>
            <async-multiselect-filter
              filter-key="city"
              :fetch-function="fetchAllPropertyCities"
              :searchable="false"
              @apply-multiselect="handleUpdateFilters"
            />
          </template>
          <template #state>
            <async-multiselect-filter
              filter-key="region"
              :fetch-function="fetchAllPropertyRegions"
              :searchable="false"
              @apply-multiselect="handleUpdateFilters"
            />
          </template>
          <template #property>
            <async-multiselect-filter
              filter-key="property_id"
              :fetch-function="fetchAllPropertyZonePropertyNames"
              :searchable="false"
              @apply-multiselect="handleUpdateFilters"
            />
          </template>
          <template #status>
            <single-select-filter
              filter-key="status"
              :items="statusFilterItems"
              @select-filter="handleUpdateFilters"
            />
          </template>
        </default-list-header>
      </template>
      <!-- eslint-disable vue/valid-v-slot -->
      <template #item.name="{ item }">
        <v-icon
          v-if="item.lowBattery"
          color="error"
          class="mr-2"
        >
          $faBatteryQuarter
        </v-icon>
        <v-tooltip
          content-class="zones-tooltip"
          bottom
          max-width="322px"
          open-delay="250"
          color="white"
          nudge-right="45px"
          z-index="10"
        >
          <template v-slot:activator="{ on, attrs }">
            <div
              class="property-name font-weight-bold"
              v-bind="attrs"
              v-on="on"
            >
              {{ item.name }}
            </div>
          </template>
          <div
            class="d-flex flex-column"
            style="color:black"
          >
            <div class="d-flex flex-column mb-3">
              <span class="zone-details-header">Gen/Revision</span>
              <span class="zone-details-text">{{ item.sensor | genRevisionFormat }}</span>
            </div>
            <div class="d-flex flex-column mb-3">
              <span class="zone-details-header">Firmware</span>
              <span class="zone-details-text">{{ item.sensor.firmware || 'unknown' }}</span>
            </div>
            <div class="d-flex flex-column">
              <span class="zone-details-header">MAC Address</span>
              <span class="zone-details-text">
                {{ item.sensor.mainMac || 'unknown' }}
              </span>
            </div>
          </div>
        </v-tooltip>
      </template>
      <template #item.property_name="{ item }">
        <router-link
          :to="{path:`/properties/${item.propertyId}`}"
          class="property-name"
        >
          {{ item.propertyName }}
        </router-link>
      </template>
      <template #item.status="{ item }">
        <span style="white-space: nowrap">
          <v-icon
            class="pb-1 pr-2"
            :color="statusColor(item.status)"
            x-small
          >
            $faCircle
          </v-icon>
          <span>
            {{ item.status || 'Unavailable' | firstCapital }}
            <span v-if="item.status && item.status.toUpperCase() === connections.OFFLINE">
              {{ item.timeOffline | timeFormat }}
            </span>
          </span>
        </span>
      </template>
      <template #item.type="{ item }">
        <span>{{ item.sensor.style || '' | firstCapital }}</span>
      </template>
      <template #item.signal="{ item }">
        <signal-strength
          :zone="{RSSI: item.RSSI, ...item.sensor}"
        />
      </template>
      <template #item.city="{ item }">
        {{ item.city }}
      </template>
      <template #item.region="{ item }">
        {{ item.region }}
      </template>
      <template slot="empty-state">
        <empty-state
          icon="$faHomeLight"
          text="No Sensors found"
        />
      </template>
    </valkyrie-list>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import listControllerMixin from '@/helpers/list-controller-mixin'
  import listMixin from '@/helpers/list-mixin'
  import ValkyrieList from '@/components/ValkyrieList.vue'
  import DefaultListHeader from '@/components/ValkyrieListDefaultHeader.vue'
  import EmptyState from '../base/EmptyState.vue'
  import AsyncMultiselectFilter from '@/components/filters/AsyncMultiselectFilter.vue'
  import paginationHelper from '@/helpers/pagination-helper'
  import SignalStrength from '@/components/activityZones/SignalStrength.vue'
  import SingleSelectFilter from '../filters/SingleSelectFilter.vue'
  import { signals, connections, getConnectionColor } from '@/helpers/sensor-connectivity.js'

  export default {
    name: 'PropertiesZonesList',
    components: {
      ValkyrieList,
      DefaultListHeader,
      EmptyState,
      AsyncMultiselectFilter,
      SignalStrength,
      SingleSelectFilter,
    },
    filters: {
      timeFormat (val) {
        if (!val) return ''
        if (val < 60) return `(${val}s)`
        if (val < 3600) return `(${Math.floor(val / 60)}m)`
        if (val < 86400) return `(${Math.floor(val / 3600)}h)`
        return `(${Math.floor(val / 86400)}d)`
      },
      firstCapital (val) {
        val = val.toLowerCase()
        return val.charAt(0).toUpperCase() + val.slice(1)
      },
      genRevisionFormat (val) {
        if (!val.generation || !val.rev) return 'unknown'
        return `${val.generation}/${val.rev}`
      },
    },
    mixins: [listMixin, listControllerMixin],
    props: {
      filterable: {
        type: Boolean,
        default: true,
      },
      hasFilters: {
        type: Boolean,
        default: false,
      },
      filterBy: {
        type: String,
        default: '',
      },
    },
    data: () => ({
      loading: false,
      search: '',
      searchable: true,
      listType: 'zones',
      payload: {
        listType: 'propertyZones',
        body: {},
        fileName: 'property_zones.csv',
      },
      headers: [
        {
          text: 'Name',
          value: 'name',
          width: 220,
          sortable: false,
        },
        {
          text: 'Property',
          value: 'property_name',
          width: 220,
        },
        {
          text: 'Status',
          value: 'status',
          width: 150,
        },
        {
          text: 'Type',
          value: 'type',
          width: 125,
          sortable: false,
        },
        {
          text: 'City',
          value: 'city',
          width: 175,
        },
        {
          text: 'State',
          value: 'region',
          width: 110,
        },
      ],
      sorted: true,
      filters: {},
      cities: [],
      regions: [],
      paginationTotal: 0,
    }),
    computed: {
      ...mapGetters({
        currentSelectedOrg: 'Permissions/currentSelectedOrg',
        canManageSelectedOrg: 'Permissions/canManageSelectedOrg',
        total: 'PropertyZones/total',
      }),
      connections () {
        return connections
      },
      propertyZonesListPagination () {
        return {
          ...this.pagination,
          total: this.total,
        }
      },
      paginationText () {
        return paginationHelper(
          this.propertyZonesListPagination.pageNum,
          this.propertyZonesListPagination.perPage,
          this.propertyZonesListPagination.total,
        )
      },
      listAPIPath () {
        return `${this.$store.state.baseURL}/properties/activityzones/list`
      },
      staticFilters () {
        if (this.currentSelectedOrg.id) {
          return [{
            key: 'organization_id',
            operator: '=',
            value: `${this.currentSelectedOrg.id}`,
          }]
        }
        return []
      },
      signalFilterItems () {
        return Object.values(signals).reduce((acc, s) => s !== signals.UNAVAILABLE ? ([...acc, { label: s, value: s }]) : acc, [])
      },
      timeOfflineFilterItems () {
        return [
          { label: 'Less than 1 hour', operator: '<=', value: 60 * 60 },
          { label: '1 day', value: 24 * 60 * 60 },
          { label: '2 days +', operator: '>=', value: 2 * 24 * 60 * 60 },
        ]
      },
      styleFilterItems () {
        return [
          { label: 'Indoor', value: 'indoor' },
          { label: 'Outdoor', value: 'outdoor' },
        ]
      },
      statusFilterItems () {
        return Object.values(connections).map(c => ({ label: c, value: c }))
      },
      filterPayload () {
        return {
          ...(this.currentSelectedOrg.id
            ? {
              filter: [
                {
                  key: 'organization_id',
                  operator: '=',
                  value: `${this.currentSelectedOrg.id}`,
                },
              ],
            }
            : {}),
        }
      },
    },
    watch: {
      currentSelectedOrg () {
        this.fetchList()
      },
    },
    created () {
      this.defaultSort = [{ key: 'property_name', order: 'ASC' }]
      this.initializeList()
    },
    methods: {
      initializeList () {
        this.fetchList().then((res) => this.updateTotal(res.data.pagination.total))
      },
      updateTotal (total) {
        this.$store.commit('PropertyZones/updatePropertyZonesTotal', total)
      },
      statusColor (connection) {
        return getConnectionColor({ connection })
      },
      goToProperty (item) {
        if (this.detail) return
        const checkRoute = this.$router.resolve({
          path: `/properties/${item.id}`,
        })
        if (checkRoute.resolved.name !== '404 Error') {
          this.$router.push({ path: `/properties/${item.id}` })
        }
      },
      fetchAllPropertyZoneTags (payload) {
        return this.$store.dispatch('PropertyZones/fetchPropertyActivityZoneListFilters', { key: 'tags', ...this.filterPayload, ...payload })
      },
      fetchAllPropertyCities (payload) {
        return this.$store.dispatch('PropertyZones/fetchPropertyActivityZoneListFilters', { key: 'city', ...this.filterPayload, ...payload })
      },
      fetchAllPropertyRegions (payload) {
        return this.$store.dispatch('PropertyZones/fetchPropertyActivityZoneListFilters', { key: 'region', ...this.filterPayload, ...payload })
      },
      fetchAllPropertyZonePropertyNames () {
        return this.$store.dispatch('PropertyZones/fetchPropertyActivityZoneListFilters', { key: 'id', ...this.filterPayload })
      },
    },
  }
</script>
<style lang="scss">
.zone-details-header {
  display:block;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.41px;
  font-family: $heading-font-family;
}
.zone-details-text {
  font-size: 12px;
}
.property-name {
  display: block;
  cursor: pointer;
  color: $muted;
  &:hover {
    text-decoration: underline;
    color: #4c6489;
  }
}
.tag {
  border-color: #535353;
  height: 24px !important;
  .v-chip__content {
    font-size: 14px;
  }
}

.zones-tooltip {
    box-shadow: 0px 1px 10px rgba($color: #000000, $alpha: 0.5) !important;
    min-width:225px;
}
.property-name {
    color: $muted;
    font-weight: 600;
    letter-spacing: 0.191002px;
    text-decoration: none;
    &:hover {
        text-decoration: none;
    }
    max-width: 50ch;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
